import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import {
  FaStar, FaCheck, FaTrash, FaSpinner,
  FaUsers, FaCheckCircle, FaTimesCircle, FaGraduationCap, FaPrint,
  FaEnvelope, FaReply,
} from 'react-icons/fa';
import EnrollmentsTable from './components/EnrollmentsTable';
import PaymentModal from './components/PaymentModal';
import StudentIdCard from './components/StudentIdCard';
import AdminLayout, { TAB_ROUTES, tabFromPath } from './components/AdminLayout';
import AdminAccounts from './components/AdminAccounts';
import AdminCourses from './components/AdminCourses';
import AdminLogin from './components/AdminLogin';
import AdminToolbar from './components/AdminToolbar';
import AdminEmptyState from './components/AdminEmptyState';
import AdminContentSkeleton from './components/AdminContentSkeleton';
import AdminListCard, { AdminBadge, AdminActionButton, AdminMeta } from './components/AdminListCard';
import {
  EnrollmentStatsPanel,
  ReviewStatsPanel,
  ContactStatsPanel,
} from './components/AdminStatsPanel';
import { SEO, ConfirmDialog, Pagination } from '../../components/common';
import {
  notifySuccess,
  notifyError,
  notifyInfo,
  NotificationMessages,
} from '../../utils/notifications';
import {
  adminLogin,
  verifyAdminToken,
  fetchAllReviews,
  fetchReviewStats,
  approveReview as approveReviewApi,
  deleteReview as deleteReviewApi,
} from '../../services/reviewService';
import {
  fetchAllCreativeEnrollments,
  fetchCreativeEnrollmentStats,
  deleteCreativeEnrollment,
  updateCreativeEnrollmentPayment,
} from '../../services/creativeEnrollmentService';
import {
  fetchAllContactMessages,
  fetchContactMessageStats,
  markContactMessageRead,
  deleteContactMessage as deleteContactMessageApi,
} from '../../services/contactService';
import { formatDate } from '../../utils/formatDate';
import { getPaymentStatus, isFullyPaid } from '../../utils/paymentStatus';

/* ─── Types ─────────────────────────────────── */

/** @typedef {'reviews'|'enrollments'|'contacts'} Tab */

const ITEMS_PER_PAGE = 10;

const ENROLLMENT_FILTER_LABELS = {
  all: 'All enrollments',
  paid: 'Fully paid',
  partial: 'Partial paid',
  unpaid: 'Unpaid only',
};

const paginateItems = (items, page, itemsPerPage) => {
  const start = (page - 1) * itemsPerPage;
  return items.slice(start, start + itemsPerPage);
};

const getTotalPages = (totalItems, itemsPerPage) =>
  Math.max(1, Math.ceil(totalItems / itemsPerPage));

const matchesSearch = (query, ...fields) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return fields.some((field) => String(field ?? '').toLowerCase().includes(normalized));
};

/* ─── Main component ─────────────────────────── */
const Admin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  /** Active tab is driven by the route: /admin/* (see TAB_ROUTES). */
  const tab = tabFromPath(location.pathname) || 'enrollments';
  const handleTabChange = useCallback((next) => navigate(TAB_ROUTES[next]), [navigate]);

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [reviewFilter, setReviewFilter] = useState('all');
  const [reviewSearch, setReviewSearch] = useState('');
  const [reviewPage, setReviewPage] = useState(1);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [refreshingReviews, setRefreshingReviews] = useState(false);
  const [processingReviewId, setProcessingReviewId] = useState(null);
  const [deleteReviewDialog, setDeleteReviewDialog] = useState({ isOpen: false, id: null });

  // Contact messages state
  const [contactMessages, setContactMessages] = useState([]);
  const [contactStats, setContactStats] = useState(null);
  const [contactFilter, setContactFilter] = useState('all');
  const [contactSearch, setContactSearch] = useState('');
  const [contactPage, setContactPage] = useState(1);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [refreshingContacts, setRefreshingContacts] = useState(false);
  const [processingContactId, setProcessingContactId] = useState(null);
  const [deleteContactDialog, setDeleteContactDialog] = useState({ isOpen: false, id: null });

  // Enrollments state
  const [enrollments, setEnrollments] = useState([]);
  const [enrollmentStats, setEnrollmentStats] = useState(null);
  const [enrollmentFilter, setEnrollmentFilter] = useState('all');
  const [enrollmentSearch, setEnrollmentSearch] = useState('');
  const [enrollmentPage, setEnrollmentPage] = useState(1);
  const [loadingEnrollments, setLoadingEnrollments] = useState(true);
  const [refreshingEnrollments, setRefreshingEnrollments] = useState(false);
  const [processingEnrollId, setProcessingEnrollId] = useState(null);
  const [deleteEnrollDialog, setDeleteEnrollDialog] = useState({ isOpen: false, id: null });
  const [paymentModalEnrollment, setPaymentModalEnrollment] = useState(null);
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  const [idCardEnrollment, setIdCardEnrollment] = useState(null);
  const pendingIdCardPrintRef = useRef(false);
  const enrollmentsPrintRef = useRef(null);
  const idCardPrintRef = useRef(null);
  const reviewsListRef = useRef(null);
  const contactsListRef = useRef(null);
  const enrollmentsListRef = useRef(null);

  const handleReviewPageChange = (next) => {
    setReviewPage(next);
    reviewsListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const handleContactPageChange = (next) => {
    setContactPage(next);
    contactsListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const handleEnrollmentPageChange = (next) => {
    setEnrollmentPage(next);
    enrollmentsListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
    setIsAuthenticated(false);
    setCredentials({ username: '', password: '' });
    notifySuccess(NotificationMessages.LOGOUT_SUCCESS);
  }, []);

  /* ── Reviews ── */
  const loadReviews = useCallback(async () => {
    setLoadingReviews(true);
    try {
      const data = await fetchAllReviews(getAuthHeaders());
      setReviews(data);
    } catch (err) {
      if (err.status === 401 || err.status === 403) { handleLogout(); return; }
      notifyError(NotificationMessages.LOADING_ERROR);
    } finally {
      setLoadingReviews(false);
    }
  }, [getAuthHeaders, handleLogout]);

  const loadReviewStats = useCallback(async () => {
    try {
      const data = await fetchReviewStats(getAuthHeaders());
      setReviewStats(data);
    } catch {/* silent */}
  }, [getAuthHeaders]);

  /* ── Contact messages ── */
  const loadContacts = useCallback(async () => {
    setLoadingContacts(true);
    try {
      const data = await fetchAllContactMessages(getAuthHeaders());
      setContactMessages(data);
    } catch (err) {
      if (err.status === 401 || err.status === 403) { handleLogout(); return; }
      notifyError(NotificationMessages.LOADING_ERROR);
    } finally {
      setLoadingContacts(false);
    }
  }, [getAuthHeaders, handleLogout]);

  const loadContactStats = useCallback(async () => {
    try {
      const data = await fetchContactMessageStats(getAuthHeaders());
      setContactStats(data);
    } catch {/* silent */}
  }, [getAuthHeaders]);

  /* ── Enrollments ── */
  const loadEnrollments = useCallback(async () => {
    setLoadingEnrollments(true);
    try {
      const data = await fetchAllCreativeEnrollments(getAuthHeaders());
      setEnrollments(data);
    } catch (err) {
      if (err.status === 401 || err.status === 403) { handleLogout(); return; }
      notifyError('Failed to load enrollments.');
    } finally {
      setLoadingEnrollments(false);
    }
  }, [getAuthHeaders, handleLogout]);

  const loadEnrollmentStats = useCallback(async () => {
    try {
      const data = await fetchCreativeEnrollmentStats(getAuthHeaders());
      setEnrollmentStats(data);
    } catch {/* silent */}
  }, [getAuthHeaders]);

  const handleRefreshReviews = useCallback(async () => {
    setRefreshingReviews(true);
    await Promise.all([loadReviews(), loadReviewStats()]);
    setRefreshingReviews(false);
  }, [loadReviews, loadReviewStats]);

  const handleRefreshContacts = useCallback(async () => {
    setRefreshingContacts(true);
    await Promise.all([loadContacts(), loadContactStats()]);
    setRefreshingContacts(false);
  }, [loadContacts, loadContactStats]);

  const handleRefreshEnrollments = useCallback(async () => {
    setRefreshingEnrollments(true);
    await Promise.all([loadEnrollments(), loadEnrollmentStats()]);
    setRefreshingEnrollments(false);
  }, [loadEnrollments, loadEnrollmentStats]);

  const verifyToken = useCallback(async (token) => {
    try {
      await verifyAdminToken({ Authorization: `Bearer ${token}` });
      setIsAuthenticated(true);
      loadReviews();
      loadReviewStats();
      loadContacts();
      loadContactStats();
      loadEnrollments();
      loadEnrollmentStats();
    } catch {
      handleLogout();
    }
  }, [loadReviews, loadReviewStats, loadContacts, loadContactStats, loadEnrollments, loadEnrollmentStats, handleLogout]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) verifyToken(token);
  }, [verifyToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) {
      notifyError('Please enter both username and password');
      return;
    }
    setIsAuthenticating(true);
    try {
      const data = await adminLogin(credentials);
      localStorage.setItem('adminToken', data.token);
      localStorage.setItem('adminUsername', data.username);
      setIsAuthenticated(true);
      notifySuccess(NotificationMessages.LOGIN_SUCCESS);
      loadReviews();
      loadReviewStats();
      loadContacts();
      loadContactStats();
      loadEnrollments();
      loadEnrollmentStats();
    } catch (err) {
      notifyError(err.message || NotificationMessages.LOGIN_ERROR);
    } finally {
      setIsAuthenticating(false);
    }
  };

  /* ── Review actions ── */
  const handleApproveReview = async (id) => {
    notifyInfo('Approving review...', { duration: 2000 });
    setProcessingReviewId(id);
    try {
      await approveReviewApi(id, getAuthHeaders());
      notifySuccess(NotificationMessages.REVIEW_APPROVED);
      loadReviews();
      loadReviewStats();
    } catch (err) {
      notifyError(err.message || 'Failed to approve review');
    } finally {
      setProcessingReviewId(null);
    }
  };

  const handleDeleteReview = async () => {
    const id = deleteReviewDialog.id;
    setDeleteReviewDialog({ isOpen: false, id: null });
    setProcessingReviewId(id);
    try {
      await deleteReviewApi(id, getAuthHeaders());
      notifySuccess(NotificationMessages.REVIEW_DELETED);
      loadReviews();
      loadReviewStats();
    } catch (err) {
      notifyError(err.message || NotificationMessages.REVIEW_DELETE_ERROR);
    } finally {
      setProcessingReviewId(null);
    }
  };

  /* ── Contact actions ── */
  const handleMarkContactRead = async (id) => {
    notifyInfo('Marking message as read...', { duration: 2000 });
    setProcessingContactId(id);
    try {
      await markContactMessageRead(id, getAuthHeaders());
      notifySuccess(NotificationMessages.CONTACT_MARKED_READ);
      loadContacts();
      loadContactStats();
    } catch (err) {
      notifyError(err.message || 'Failed to update message');
    } finally {
      setProcessingContactId(null);
    }
  };

  const handleDeleteContact = async () => {
    const id = deleteContactDialog.id;
    setDeleteContactDialog({ isOpen: false, id: null });
    setProcessingContactId(id);
    try {
      await deleteContactMessageApi(id, getAuthHeaders());
      notifySuccess(NotificationMessages.CONTACT_DELETED);
      loadContacts();
      loadContactStats();
    } catch (err) {
      notifyError(err.message || NotificationMessages.CONTACT_DELETE_ERROR);
    } finally {
      setProcessingContactId(null);
    }
  };

  /* ── Enrollment actions ── */
  const handleDeleteEnrollment = async () => {
    const id = deleteEnrollDialog.id;
    setDeleteEnrollDialog({ isOpen: false, id: null });
    setProcessingEnrollId(id);
    try {
      await deleteCreativeEnrollment(id, getAuthHeaders());
      notifySuccess('Enrollment deleted.');
      setEnrollments((prev) => prev.filter((e) => e.id !== id));
      if (enrollmentStats) {
        setEnrollmentStats((prev) => ({
          ...prev,
          total_enrollments: (prev.total_enrollments || 1) - 1,
        }));
      }
      loadEnrollmentStats();
    } catch (err) {
      notifyError(err.message || 'Failed to delete enrollment.');
    } finally {
      setProcessingEnrollId(null);
    }
  };

  const handleRecordPayment = (enrollment) => {
    setPaymentModalEnrollment(enrollment);
  };

  const handleSubmitPayment = async (enrollment, { amountPaid, discountAmount }) => {
    setIsSavingPayment(true);
    setProcessingEnrollId(enrollment.id);
    try {
      const data = await updateCreativeEnrollmentPayment(
        enrollment.id,
        { amountPaid, discountAmount },
        getAuthHeaders()
      );
      setEnrollments((prev) =>
        prev.map((e) => (e.id === enrollment.id ? { ...e, ...data.data } : e))
      );
      setPaymentModalEnrollment(null);
      loadEnrollmentStats();
      notifySuccess(data.message || 'Payment updated.');
    } catch (err) {
      notifyError(err.message || 'Failed to update payment.');
    } finally {
      setIsSavingPayment(false);
      setProcessingEnrollId(null);
    }
  };

  const handlePrintIdCard = useReactToPrint({
    contentRef: idCardPrintRef,
    documentTitle: () => `Student ID - ${idCardEnrollment?.student_id || 'Card'}`,
    pageStyle: `
      @page {
        size: 3.375in 2.125in;
        margin: 0;
      }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          width: 3.375in !important;
          height: 2.125in !important;
          overflow: hidden !important;
          background: #0f0f1a !important;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
      }
    `,
    onAfterPrint: () => setIdCardEnrollment(null),
  });

  const onPrintIdCard = useCallback((enrollment) => {
    if (!isFullyPaid(enrollment)) {
      notifyInfo('ID card is available only after full payment.');
      return;
    }
    pendingIdCardPrintRef.current = true;
    setIdCardEnrollment(enrollment);
  }, []);

  useEffect(() => {
    if (!pendingIdCardPrintRef.current || !idCardEnrollment) return undefined;

    pendingIdCardPrintRef.current = false;
    const timer = window.setTimeout(() => {
      handlePrintIdCard();
    }, 150);

    return () => window.clearTimeout(timer);
  }, [idCardEnrollment, handlePrintIdCard]);

  const filteredReviews = useMemo(() => {
    return reviews.filter((r) => {
      if (reviewFilter === 'pending' && r.is_approved) return false;
      if (reviewFilter === 'approved' && !r.is_approved) return false;
      return matchesSearch(reviewSearch, r.name, r.comment);
    });
  }, [reviews, reviewFilter, reviewSearch]);

  const reviewTotalPages = getTotalPages(filteredReviews.length, ITEMS_PER_PAGE);
  const paginatedReviews = useMemo(
    () => paginateItems(filteredReviews, reviewPage, ITEMS_PER_PAGE),
    [filteredReviews, reviewPage]
  );

  const filteredContacts = useMemo(() => {
    return contactMessages.filter((message) => {
      if (contactFilter === 'unread' && message.is_read) return false;
      if (contactFilter === 'read' && !message.is_read) return false;
      return matchesSearch(contactSearch, message.name, message.email, message.subject, message.message);
    });
  }, [contactMessages, contactFilter, contactSearch]);

  const contactTotalPages = getTotalPages(filteredContacts.length, ITEMS_PER_PAGE);
  const paginatedContacts = useMemo(
    () => paginateItems(filteredContacts, contactPage, ITEMS_PER_PAGE),
    [filteredContacts, contactPage]
  );

  const filteredEnrollments = useMemo(() => {
    return enrollments.filter((e) => {
      const status = getPaymentStatus(e);
      if (enrollmentFilter === 'paid' && status !== 'paid') return false;
      if (enrollmentFilter === 'partial' && status !== 'partial') return false;
      if (enrollmentFilter === 'unpaid' && status !== 'unpaid') return false;
      return matchesSearch(enrollmentSearch, e.student_id, e.name, e.email, e.phone, e.level, e.goal);
    });
  }, [enrollments, enrollmentFilter, enrollmentSearch]);

  const enrollmentTotalPages = getTotalPages(filteredEnrollments.length, ITEMS_PER_PAGE);
  const paginatedEnrollments = useMemo(
    () => paginateItems(filteredEnrollments, enrollmentPage, ITEMS_PER_PAGE),
    [filteredEnrollments, enrollmentPage]
  );

  useEffect(() => {
    setReviewPage(1);
  }, [reviewFilter, reviewSearch]);

  useEffect(() => {
    setContactPage(1);
  }, [contactFilter, contactSearch]);

  useEffect(() => {
    setEnrollmentPage(1);
  }, [enrollmentFilter, enrollmentSearch]);

  useEffect(() => {
    if (reviewPage > reviewTotalPages) setReviewPage(reviewTotalPages);
  }, [reviewPage, reviewTotalPages]);

  useEffect(() => {
    if (contactPage > contactTotalPages) setContactPage(contactTotalPages);
  }, [contactPage, contactTotalPages]);

  useEffect(() => {
    if (enrollmentPage > enrollmentTotalPages) setEnrollmentPage(enrollmentTotalPages);
  }, [enrollmentPage, enrollmentTotalPages]);

  const handlePrintEnrollments = useReactToPrint({
    contentRef: enrollmentsPrintRef,
    documentTitle: 'Course Enrollments Report',
    pageStyle: `
      @page {
        size: A4 landscape;
        margin: 10mm 12mm;
      }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
        }
        body, body * {
          color: #000 !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        a { color: #000 !important; text-decoration: none !important; }

        .enrollments-print-root {
          background: #fff !important;
        }

        .enrollments-print-page {
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          min-height: 190mm;
          padding: 0;
          page-break-after: always;
          break-after: page;
        }

        .enrollments-print-page:last-child {
          page-break-after: auto;
          break-after: auto;
        }

        .print-page-header {
          flex-shrink: 0;
          margin-bottom: 4mm;
          padding-bottom: 3mm;
          border-bottom: 1px solid #000;
        }

        .print-page-header-compact {
          margin-bottom: 3mm;
          padding-bottom: 2mm;
        }

        .print-page-content {
          flex: 1 1 auto;
          min-height: 0;
        }

        .print-data-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
        }

        .print-table-head {
          display: table-header-group;
        }

        .print-table-row {
          break-inside: avoid;
          page-break-inside: avoid;
        }

        th, td {
          padding: 2mm 1.5mm !important;
          text-align: left;
          color: #000 !important;
          border-color: #ccc !important;
          vertical-align: top;
          word-wrap: break-word;
          overflow-wrap: anywhere;
        }

        .print-page-footer {
          flex-shrink: 0;
          margin-top: auto;
          padding-top: 3mm;
          border-top: 1px solid #999;
          text-align: center;
          font-size: 8pt;
          color: #000 !important;
        }

        .print-page-footer-sep {
          margin: 0 0.4em;
          color: #666 !important;
        }

        .print-financial-summary {
          max-width: 80mm;
          margin: 0 auto;
          padding-top: 4mm;
        }

        .print-summary-grid {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 2mm 6mm;
        }

        .enrollments-print-summary-page {
          page-break-before: always;
          break-before: page;
        }
      }
    `,
  });

  const enrollmentPrintMeta = useMemo(() => ({
    generatedAt: formatDate(new Date().toISOString(), 'withTime'),
    filterLabel: ENROLLMENT_FILTER_LABELS[enrollmentFilter] || 'All enrollments',
    searchQuery: enrollmentSearch.trim(),
  }), [enrollmentFilter, enrollmentSearch]);

  /* ══════════════ Login screen ══════════════ */
  if (!isAuthenticated) {
    return (
      <>
        <SEO title="Admin Login | Arvix Creative" description="Admin panel login" keywords="admin, login, arvix creative" />
        <AdminLogin
          credentials={credentials}
          onChange={setCredentials}
          onSubmit={handleLogin}
          isAuthenticating={isAuthenticating}
        />
      </>
    );
  }

  const adminStats = {
    enrollments: enrollmentStats?.total_enrollments,
    contacts: contactStats?.total_messages,
    reviews: reviewStats?.total_reviews,
  };

  /* ══════════════ Admin Dashboard ══════════════ */
  return (
    <>
      <SEO title="Admin Panel | Arvix Creative" description="Manage reviews, contact messages, and course enrollments" keywords="admin, arvix creative" />

      <AdminLayout
        activeTab={tab}
        onTabChange={handleTabChange}
        onLogout={handleLogout}
        username={localStorage.getItem('adminUsername')}
        stats={adminStats}
      >
        {tab === 'accounts' && <AdminAccounts getHeaders={getAuthHeaders} />}

        {tab === 'courses' && <AdminCourses getHeaders={getAuthHeaders} />}

        {tab === 'reviews' && (
          <div className="animate-fadeIn">
            {reviewStats && <ReviewStatsPanel stats={reviewStats} />}

            <AdminToolbar
              filters={[
                { key: 'all', label: 'All', count: reviews.length, activeClass: 'bg-designer-dark text-white' },
                { key: 'pending', label: 'Pending', count: reviews.filter((r) => !r.is_approved).length, activeClass: 'bg-amber-500 text-white' },
                { key: 'approved', label: 'Approved', count: reviews.filter((r) => r.is_approved).length, activeClass: 'bg-green-600 text-white' },
              ]}
              activeFilter={reviewFilter}
              onFilterChange={setReviewFilter}
              searchValue={reviewSearch}
              onSearchChange={setReviewSearch}
              searchPlaceholder="Search by name or comment..."
              onRefresh={handleRefreshReviews}
              isRefreshing={refreshingReviews}
              isLoading={loadingReviews}
            />

            {loadingReviews ? (
              <AdminContentSkeleton rows={4} />
            ) : filteredReviews.length === 0 ? (
              <AdminEmptyState
                icon={FaStar}
                title={reviews.length === 0 ? 'No reviews yet' : 'No matching reviews'}
                description={reviews.length === 0
                  ? 'Submitted reviews will appear here once available.'
                  : 'Try adjusting your search or filter.'}
              />
            ) : (
              <>
                <p className="text-xs text-gray-400 mb-3">
                  Showing {(reviewPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(reviewPage * ITEMS_PER_PAGE, filteredReviews.length)} of {filteredReviews.length}
                </p>
                <div ref={reviewsListRef} className="space-y-3 scroll-mt-6">
                  {paginatedReviews.map((review) => (
                    <AdminListCard
                      key={review.id}
                      borderAccent={review.is_approved ? 'border-green-200/80' : 'border-amber-200/80'}
                      actions={
                        <>
                          {!review.is_approved && (
                            <AdminActionButton
                              variant="success"
                              onClick={() => handleApproveReview(review.id)}
                              disabled={processingReviewId === review.id}
                              icon={processingReviewId === review.id ? <FaSpinner className="animate-spin" /> : <FaCheck className="w-3.5 h-3.5" />}
                            >
                              Approve
                            </AdminActionButton>
                          )}
                          <AdminActionButton
                            variant="danger"
                            onClick={() => setDeleteReviewDialog({ isOpen: true, id: review.id })}
                            disabled={processingReviewId === review.id}
                            icon={processingReviewId === review.id ? <FaSpinner className="animate-spin w-3.5 h-3.5" /> : <FaTrash className="w-3.5 h-3.5" />}
                            title="Delete"
                          />
                        </>
                      }
                    >
                      <div className="flex flex-wrap items-center gap-2.5 mb-2">
                        <h3 className="text-base font-semibold text-designer-dark break-all">{review.name}</h3>
                        <div className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <FaStar key={i} className={`text-sm ${i < review.rating ? 'text-yellow-400' : 'text-gray-200'}`} />
                          ))}
                        </div>
                        <AdminBadge variant={review.is_approved ? 'success' : 'warning'}>
                          {review.is_approved ? <FaCheckCircle className="w-3 h-3" /> : <FaTimesCircle className="w-3 h-3" />}
                          {review.is_approved ? 'Approved' : 'Pending'}
                        </AdminBadge>
                      </div>
                      <p className="text-sm text-gray-600 break-words leading-relaxed">{review.comment}</p>
                      <AdminMeta>Submitted {formatDate(review.created_at, 'withTime')}</AdminMeta>
                    </AdminListCard>
                  ))}
                </div>
                <Pagination currentPage={reviewPage} totalPages={reviewTotalPages} onPageChange={handleReviewPageChange} />
              </>
            )}
          </div>
        )}

        {tab === 'contacts' && (
          <div className="animate-fadeIn">
            {contactStats && <ContactStatsPanel stats={contactStats} />}

            <AdminToolbar
              filters={[
                { key: 'all', label: 'All', count: contactMessages.length, activeClass: 'bg-designer-dark text-white' },
                { key: 'unread', label: 'Unread', count: contactMessages.filter((m) => !m.is_read).length, activeClass: 'bg-amber-500 text-white' },
                { key: 'read', label: 'Read', count: contactMessages.filter((m) => m.is_read).length, activeClass: 'bg-green-600 text-white' },
              ]}
              activeFilter={contactFilter}
              onFilterChange={setContactFilter}
              searchValue={contactSearch}
              onSearchChange={setContactSearch}
              searchPlaceholder="Search name, email, subject..."
              onRefresh={handleRefreshContacts}
              isRefreshing={refreshingContacts}
              isLoading={loadingContacts}
            />

            {loadingContacts ? (
              <AdminContentSkeleton rows={4} />
            ) : filteredContacts.length === 0 ? (
              <AdminEmptyState
                icon={FaEnvelope}
                title={contactMessages.length === 0 ? 'No contact messages yet' : 'No matching messages'}
                description={contactMessages.length === 0
                  ? 'Messages from the contact form will appear here.'
                  : 'Try adjusting your search or filter.'}
              />
            ) : (
              <>
                <p className="text-xs text-gray-400 mb-3">
                  Showing {(contactPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(contactPage * ITEMS_PER_PAGE, filteredContacts.length)} of {filteredContacts.length}
                </p>
                <div ref={contactsListRef} className="space-y-3 scroll-mt-6">
                  {paginatedContacts.map((message) => (
                    <AdminListCard
                      key={message.id}
                      borderAccent={message.is_read ? 'border-green-200/80' : 'border-amber-200/80'}
                      actions={
                        <>
                          <AdminActionButton
                            variant="info"
                            href={`mailto:${message.email}?subject=Re: ${encodeURIComponent(message.subject)}&body=${encodeURIComponent(`Hi ${message.name},\n\nThank you for contacting Arvix Creative.\n\n`)}`}
                            icon={<FaReply className="w-3.5 h-3.5" />}
                            title="Reply via email"
                          >
                            Reply
                          </AdminActionButton>
                          {!message.is_read && (
                            <AdminActionButton
                              variant="success"
                              onClick={() => handleMarkContactRead(message.id)}
                              disabled={processingContactId === message.id}
                              icon={processingContactId === message.id ? <FaSpinner className="animate-spin" /> : <FaCheck className="w-3.5 h-3.5" />}
                            >
                              Mark Read
                            </AdminActionButton>
                          )}
                          <AdminActionButton
                            variant="danger"
                            onClick={() => setDeleteContactDialog({ isOpen: true, id: message.id })}
                            disabled={processingContactId === message.id}
                            icon={processingContactId === message.id ? <FaSpinner className="animate-spin w-3.5 h-3.5" /> : <FaTrash className="w-3.5 h-3.5" />}
                            title="Delete"
                          />
                        </>
                      }
                    >
                      <div className="flex flex-wrap items-center gap-2.5 mb-2">
                        <h3 className="text-base font-semibold text-designer-dark break-all">{message.name}</h3>
                        <a
                          href={`mailto:${message.email}?subject=Re: ${encodeURIComponent(message.subject)}`}
                          className="text-xs text-designer-primary hover:underline break-all"
                        >
                          {message.email}
                        </a>
                        <AdminBadge variant="danger">{message.subject}</AdminBadge>
                        <AdminBadge variant={message.is_read ? 'success' : 'warning'}>
                          {message.is_read ? <FaCheckCircle className="w-3 h-3" /> : <FaTimesCircle className="w-3 h-3" />}
                          {message.is_read ? 'Read' : 'Unread'}
                        </AdminBadge>
                      </div>
                      <p className="text-sm text-gray-600 break-words whitespace-pre-wrap leading-relaxed">{message.message}</p>
                      <AdminMeta>Received {formatDate(message.created_at, 'withTime')}</AdminMeta>
                    </AdminListCard>
                  ))}
                </div>
                <Pagination currentPage={contactPage} totalPages={contactTotalPages} onPageChange={handleContactPageChange} />
              </>
            )}
          </div>
        )}

        {tab === 'enrollments' && (
          <div className="animate-fadeIn">
            {enrollmentStats && (
              <>
                <EnrollmentStatsPanel stats={enrollmentStats} />

                {enrollmentStats.by_level?.length > 0 && (
                  <div className="bg-white rounded-xl p-5 border border-gray-200/80 shadow-sm mb-5">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <FaGraduationCap className="text-designer-primary" />
                      Enrollment by Experience Level
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {enrollmentStats.by_level.map(({ level, count }) => {
                        const pct = enrollmentStats.total_enrollments > 0
                          ? Math.round((count / enrollmentStats.total_enrollments) * 100)
                          : 0;
                        return (
                          <div key={level} className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="font-medium text-designer-dark truncate">{level}</span>
                              <span className="font-bold text-designer-primary ml-2 shrink-0 tabular-nums">{count}</span>
                            </div>
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-designer-primary rounded-full transition-all duration-700"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <p className="text-[11px] text-gray-400">{pct}% of total</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            <AdminToolbar
              filters={[
                { key: 'all', label: 'All', count: enrollments.length, activeClass: 'bg-designer-dark text-white' },
                { key: 'paid', label: 'Paid', count: enrollments.filter((e) => getPaymentStatus(e) === 'paid').length, activeClass: 'bg-green-600 text-white' },
                { key: 'partial', label: 'Partial', count: enrollments.filter((e) => getPaymentStatus(e) === 'partial').length, activeClass: 'bg-blue-600 text-white' },
                { key: 'unpaid', label: 'Unpaid', count: enrollments.filter((e) => getPaymentStatus(e) === 'unpaid').length, activeClass: 'bg-amber-500 text-white' },
              ]}
              activeFilter={enrollmentFilter}
              onFilterChange={setEnrollmentFilter}
              searchValue={enrollmentSearch}
              onSearchChange={setEnrollmentSearch}
              searchPlaceholder="Search name, ID, email, phone..."
              onRefresh={handleRefreshEnrollments}
              isRefreshing={refreshingEnrollments}
              isLoading={loadingEnrollments}
              actions={
                <button
                  type="button"
                  onClick={() => {
                    if (filteredEnrollments.length === 0) {
                      notifyInfo('No enrollments to print.');
                      return;
                    }
                    handlePrintEnrollments();
                  }}
                  disabled={loadingEnrollments || filteredEnrollments.length === 0}
                  className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-600 hover:text-designer-primary rounded-xl border border-gray-200/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed shrink-0 text-xs font-medium"
                  title="Print all matching enrollments"
                >
                  <FaPrint className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Print</span>
                </button>
              }
            />

            {loadingEnrollments ? (
              <AdminContentSkeleton rows={6} variant="table" />
            ) : filteredEnrollments.length === 0 ? (
              <AdminEmptyState
                icon={FaUsers}
                title={enrollments.length === 0 ? 'No enrollments yet' : 'No matching enrollments'}
                description={enrollments.length === 0
                  ? 'Enrollments from the Creative Samurai landing page will appear here.'
                  : 'Try adjusting your search or filter.'}
              />
            ) : (
              <>
                <div ref={enrollmentsListRef} className="flex items-center justify-between mb-3 scroll-mt-6">
                  <p className="text-xs text-gray-400">
                    Showing {(enrollmentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(enrollmentPage * ITEMS_PER_PAGE, filteredEnrollments.length)} of {filteredEnrollments.length}
                  </p>
                  <p className="text-[11px] text-gray-400 hidden sm:block">
                    Print includes all {filteredEnrollments.length} matching records
                  </p>
                </div>

                <EnrollmentsTable
                  enrollments={paginatedEnrollments}
                  allEnrollments={filteredEnrollments}
                  processingEnrollId={processingEnrollId}
                  onRecordPayment={handleRecordPayment}
                  onPrintIdCard={onPrintIdCard}
                  onDelete={(id) => setDeleteEnrollDialog({ isOpen: true, id })}
                  printMeta={enrollmentPrintMeta}
                  printRef={enrollmentsPrintRef}
                />

                <Pagination
                  currentPage={enrollmentPage}
                  totalPages={enrollmentTotalPages}
                  onPageChange={handleEnrollmentPageChange}
                />
              </>
            )}
          </div>
        )}
      </AdminLayout>

      {/* Dialogs */}
      <ConfirmDialog
        isOpen={deleteReviewDialog.isOpen}
        onClose={() => { setDeleteReviewDialog({ isOpen: false, id: null }); notifyInfo('Deletion cancelled', { duration: 2000 }); }}
        onConfirm={handleDeleteReview}
        title="Delete Review"
        message="Are you sure you want to delete this review? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
      <ConfirmDialog
        isOpen={deleteContactDialog.isOpen}
        onClose={() => { setDeleteContactDialog({ isOpen: false, id: null }); notifyInfo('Deletion cancelled', { duration: 2000 }); }}
        onConfirm={handleDeleteContact}
        title="Delete Message"
        message="Are you sure you want to delete this contact message? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
      <ConfirmDialog
        isOpen={deleteEnrollDialog.isOpen}
        onClose={() => setDeleteEnrollDialog({ isOpen: false, id: null })}
        onConfirm={handleDeleteEnrollment}
        title="Delete Enrollment"
        message="Are you sure you want to delete this enrollment record? This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      <PaymentModal
        isOpen={Boolean(paymentModalEnrollment)}
        enrollment={paymentModalEnrollment}
        isSubmitting={isSavingPayment}
        onClose={() => !isSavingPayment && setPaymentModalEnrollment(null)}
        onSubmit={handleSubmitPayment}
      />

      <div
        aria-hidden="true"
        className="pointer-events-none fixed overflow-hidden"
        style={{ left: '-9999px', top: 0, width: '3.375in', height: '2.125in' }}
      >
        <StudentIdCard ref={idCardPrintRef} enrollment={idCardEnrollment} />
      </div>
    </>
  );
};

export default Admin;
